/**
 * Sözel akıcılık: kelime merdiveni ve kurallı kelime üretme.
 *
 * İkisinin puanlanabilirliği taban tabana zıt ve bu, ikisinin kanalını
 * da belirliyor:
 *
 * - **Merdiven** kapalı bir havuzdan seçim yaptırıyor; "tek harf
 *   değiştir, anlamlı kelime olsun" iddiası havuza bakılarak
 *   denetlenebiliyor. Dolayısıyla ekranda da kâğıtta da otomatik
 *   puanlanıyor.
 * - **Kurallı üretme** açık uçlu. Çocuğun yazdığı kelimenin gerçek bir
 *   nesne adı olup olmadığını elimizdeki 70 kelimelik tanık listesiyle
 *   karara bağlamak, listenin eksikliğini çocuğun hatası gibi
 *   göstermek olur. O yüzden YALNIZCA KÂĞIT ve elle puanlanıyor;
 *   yazılım kuralın cevaplanabilir olduğunu garanti etmekle yetiniyor.
 *
 * İkincisi için bir dil modeli çağırmak akla geliyor (ve istek listesinde
 * öyle yazıyordu) ama puanlama sunucuda ve determinist: aynı cevap iki
 * kez aynı puanı almak zorunda. Bir dil modeli bu sözü veremiyor.
 */

import { Rng } from '../../prng'
import { foldTr, trUpper } from '../helpers'
import { DORT_HARF, NESNELER, merdivenCizgesi, tekHarfFarki } from '../sozluk'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult, RenderNode } from '../types'

/* ================================================================== *
 * 23. Kelime merdiveni
 *
 * KEDİ'den KAPI'ya değil: bizim havuzumuz dört harfli ve komşuluk
 * (tek harf farkı) seyrek bir ilişki olduğu için yollar ancak bağlı
 * öbekler içinde var. Öbekler `sozluk.ts`'te derlendi; çizge üretim
 * anında kuruluyor, elle yazılmıyor.
 *
 * HAVUZ ÇOCUĞA GÖSTERİLİYOR. Göstermemek "aklına gelen dört harfli
 * kelimeler" oyunu olurdu ve o oyunun cevabı denetlenemez. Kapalı
 * kümeden seçim yapmak hem puanlanabilir hem de görevi asıl olması
 * gereken şeye indiriyor: hangi sırayla gidileceğini planlamak.
 * ================================================================== */
export const wordLadder: ExerciseTemplate = {
  code: 'word_ladder',
  title: 'Kelime merdiveni',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Çocuğun yazacağı ara kelime sayısı */
      adim: scale(l, 2, 4),
      /** Kaç merdiven var */
      merdiven: l >= 5 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const adim = p['adim'] as number
    const kac = p['merdiven'] as number
    const cizge = merdivenCizgesi(DORT_HARF)

    const merdivenler: { yol: string[]; havuz: string[] }[] = []
    const kullanilanBas = new Set<string>()
    for (let m = 0; m < kac; m++) {
      let bulunan: { yol: string[]; havuz: string[] } | null = null
      for (let deneme = 0; deneme < 300 && !bulunan; deneme++) {
        const bas = rng.pick(DORT_HARF)
        if (kullanilanBas.has(bas)) continue
        const yol = yolAra(cizge, bas, adim + 2, rng)
        if (!yol) continue
        /**
         * Hedef, başlangıcın KOMŞUSU olmasın.
         *
         * Düzey 9 provasında "KAZA → KARA, 5 adım" çıktı ve ikisi tek
         * harf farkıyla zaten komşuydu: çocuk hedefi doğrudan yazıp
         * sayfanın geri kalanına bakıyor. Görev tam adım sayısıyla
         * gitmek, ama bunu bir sayfada savunmanın yolu hedefi uzağa
         * koymaktan geçiyor.
         */
        if (enKisa(cizge, bas, yol[yol.length - 1]!) < 2) continue
        bulunan = { yol, havuz: bilesen(cizge, bas) }
      }
      if (!bulunan) continue
      kullanilanBas.add(bulunan.yol[0]!)
      merdivenler.push(bulunan)
    }

    const render: RenderNode[] = []
    const cevaplar: { id: string; beklenen: string; bas: string; hedef: string; havuz: string[] }[] =
      []
    merdivenler.forEach((md, mi) => {
      const bas = md.yol[0]!
      const hedef = md.yol[md.yol.length - 1]!
      render.push({
        t: 'text',
        value: `${trUpper(bas)} → ${trUpper(hedef)}`,
        size: 'lg',
        bold: true,
      })
      render.push({
        t: 'text',
        value: `Kullanabileceğin kelimeler: ${md.havuz.join(', ')}`,
        size: 'sm',
      })
      const ara = md.yol.slice(1, -1)
      render.push({
        t: 'rows',
        items: ara.map((_, i) => ({ label: `${i + 1}. adım`, blank: true, id: `m${mi}_${i}` })),
        columns: 1,
      })
      ara.forEach((w, i) => {
        cevaplar.push({ id: `m${mi}_${i}`, beklenen: w, bas, hedef, havuz: md.havuz })
      })
    })

    return {
      code: 'word_ladder',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      /**
       * Adım sayısı yönergede AÇIKÇA yazıyor.
       *
       * Havuzun çapı küçük: en uzak iki kelime arası üç adım. Yani
       * "hedefe ulaş" diye sorulsaydı çoğu sayfada cevap iki adımda
       * biter ve düzey hiç artmazdı. Görev bu yüzden "tam olarak şu
       * kadar adımda git" — planlama görevi olarak da en kısa yolu
       * bulmaktan zor. Yazmasaydık çocuk kısa yoldan gidip boş satır
       * bırakır ve sayfayı yanlış anlamış sayılırdı.
       */
      prompt:
        `Her adımda SADECE BİR harf değiştir ve listedeki kelimelerden birine geç. ` +
        `Tam olarak ${adim} adımda hedefe ulaş; son adımdan hedefe de tek harfle gidilmeli.`,
      spokenPrompt:
        'Baştaki kelimeden hedefe gideceksin. Her adımda tek bir harf değişecek ve ' +
        'yazdığın her kelime listede olacak.',
      render,
      solution: cevaplar,
      grading: 'auto',
      estimatedSeconds: cevaplar.length * 45,
      itemCount: cevaplar.length,
    }
  },
  /**
   * Puanlama kanonik yolu DEĞİL, geçerli olan her yolu kabul ediyor.
   *
   * Aynı başlangıç ve hedef arasında birkaç yol var; kanonik çözümle
   * karşılaştırmak, çocuğun bulduğu başka bir doğru yolu yanlış saymak
   * olurdu. Her adım, ÇOCUĞUN kendi önceki adımına göre denetleniyor:
   * bir basamak bozulduğunda geri kalanı peşi sıra düşmüyor, ama son
   * adımın hedefe bağlanması ayrıca aranıyor.
   */
  grade(instance, answer): GradeResult {
    const beklenen = instance.solution as {
      id: string
      beklenen: string
      bas: string
      hedef: string
      havuz: string[]
    }[]
    const got = Array.isArray(answer) ? answer : []
    const items: ItemResult[] = []
    let onceki = beklenen[0]?.bas ?? ''
    let merdivenAdi = ''
    const kullanilan = new Set<string>()
    beklenen.forEach((b, i) => {
      if (b.bas !== merdivenAdi) {
        merdivenAdi = b.bas
        onceki = b.bas
        kullanilan.clear()
        kullanilan.add(foldTr(b.bas))
      }
      const ham = got[i]
      const verilen = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      const kelime = verilen ? foldTr(verilen) : null
      const sonAdim = beklenen[i + 1]?.bas !== b.bas
      const havuzda = kelime !== null && b.havuz.some((w) => foldTr(w) === kelime)
      const dogru =
        kelime !== null &&
        havuzda &&
        !kullanilan.has(kelime) &&
        tekHarfFarki(foldTr(onceki), kelime) &&
        (!sonAdim || tekHarfFarki(kelime, foldTr(b.hedef)))
      items.push({
        id: b.id,
        beklenen: sonAdim ? `${b.beklenen} (hedefe bağlanacak)` : b.beklenen,
        verilen,
        dogru,
      })
      if (kelime !== null) {
        kullanilan.add(kelime)
        onceki = kelime
      }
    })
    const perItem = items.map((x) => x.dogru)
    const correct = perItem.filter(Boolean).length
    return {
      correct,
      total: items.length,
      accuracy: items.length ? correct / items.length : 0,
      perItem,
      items,
    }
  },
  /**
   * Anahtar BİR çözüm, tek çözüm değil.
   *
   * Uygulayıcı anahtarla karşılaştırıp farklı bir yolu yanlış saymasın
   * diye bu cümle anahtarın içinde yazıyor.
   */
  anahtar(instance) {
    const sol = instance.solution as { beklenen: string; bas: string; hedef: string }[]
    const yollar = new Map<string, { hedef: string; ara: string[] }>()
    for (const s of sol) {
      if (!yollar.has(s.bas)) yollar.set(s.bas, { hedef: s.hedef, ara: [] })
      yollar.get(s.bas)!.ara.push(s.beklenen)
    }
    const satirlar = [...yollar.entries()].map(
      ([bas, y]) => `${trUpper(bas)} → ${y.ara.join(' → ')} → ${trUpper(y.hedef)}`,
    )
    return `${satirlar.join('   |   ')}  (bir çözüm; tek harf kuralına uyan başka yollar da doğru)`
  },
}

/** Verilen kelimeden başlayan, tam `uzunluk` düğümlü basit yol. */
function yolAra(
  cizge: Map<string, string[]>,
  bas: string,
  uzunluk: number,
  rng: Rng,
): string[] | null {
  const yol = [bas]
  const gorulen = new Set([bas])
  const ilerle = (): boolean => {
    if (yol.length === uzunluk) return true
    const komsular = rng.shuffle(cizge.get(yol[yol.length - 1]!) ?? [])
    for (const k of komsular) {
      if (gorulen.has(k)) continue
      yol.push(k)
      gorulen.add(k)
      if (ilerle()) return true
      yol.pop()
      gorulen.delete(k)
    }
    return false
  }
  return ilerle() ? yol : null
}

/** İki kelime arasındaki en kısa adım sayısı; ulaşılamıyorsa sonsuz. */
function enKisa(cizge: Map<string, string[]>, a: string, b: string): number {
  if (a === b) return 0
  const uzaklik = new Map([[a, 0]])
  const kuyruk = [a]
  while (kuyruk.length) {
    const x = kuyruk.shift()!
    for (const y of cizge.get(x) ?? []) {
      if (uzaklik.has(y)) continue
      uzaklik.set(y, uzaklik.get(x)! + 1)
      if (y === b) return uzaklik.get(y)!
      kuyruk.push(y)
    }
  }
  return Number.POSITIVE_INFINITY
}

/** Kelimenin bağlı olduğu öbek: çocuğa gösterilen havuz. */
function bilesen(cizge: Map<string, string[]>, bas: string): string[] {
  const gorulen = new Set<string>()
  const yigin = [bas]
  while (yigin.length) {
    const x = yigin.pop()!
    if (gorulen.has(x)) continue
    gorulen.add(x)
    for (const y of cizge.get(x) ?? []) if (!gorulen.has(y)) yigin.push(y)
  }
  return [...gorulen].sort()
}

/* ================================================================== *
 * 24. Kurallı kelime üretme
 *
 * "B ile başlayan, içinde E geçmeyen, beş harfli nesneler." Üç kısıt
 * birden tutmak, akıcılığı ketleme ile birlikte çalıştırıyor.
 *
 * YALNIZCA KÂĞIT ve ELLE PUANLANIYOR — gerekçe dosya başında.
 *
 * Yazılımın asıl işi burada kuralı denetlemek: her kural, tanık
 * havuzunda en az üç cevabı olduğu gösterilerek seçiliyor. Cevabı
 * olmayan bir yönerge çocuğa kendi yetersizliğini anlatıyor ve bunun
 * sayfada hiçbir izi kalmıyor.
 * ================================================================== */
/**
 * Yalnızca küçük harfe çevirir; Türkçe işaretleri KORUR.
 *
 * `foldTr` burada kullanılamaz ve bu bir denemede ortaya çıktı: ç→c
 * katladığı için "Ç harfiyle başlayan, 5 harfli nesneler" kuralının
 * tanıkları arasına **ceket** giriyordu. Tanık listesi cevap
 * anahtarına örnek olarak basılıyor, yani uygulayıcıya kurala uymayan
 * bir kelimeyi doğru diye gösteriyorduk.
 *
 * `foldTr`'nin yeri puanlama: çocuk "araç" yerine "arac" yazdığında
 * yanlış dememek için. Kuralın kendisi ise harfin TAM olarak ne
 * olduğuyla ilgili — ölçtüğümüz şey tam olarak o harfle başlayan
 * kelimeleri bulabilmek.
 */
function kucuk(s: string): string {
  return s.toLocaleLowerCase('tr-TR')
}

export const wordFluency: ExerciseTemplate = {
  code: 'word_fluency',
  title: 'Kurallı kelime üretme',
  domains: ['working_memory_verbal', 'inhibition'],
  channels: ['print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç ayrı kural */
      kural: l >= 6 ? 3 : l >= 3 ? 2 : 1,
      /** Kural başına kaç satır */
      satir: scale(l, 4, 8),
      /** Uzunluk kısıtı 3., yasak harf 6. düzeyden sonra */
      uzunlukKisiti: l >= 3,
      yasakHarf: l >= 6,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const kacKural = p['kural'] as number
    const satir = p['satir'] as number

    const BAS_HARFLER = ['B', 'K', 'S', 'T', 'A', 'Ç', 'D', 'M', 'P', 'Y']
    const YASAKLAR = ['E', 'A', 'İ', 'L', 'R']

    const kurallar: { metin: string; tanik: string[] }[] = []
    const gorulenBas = new Set<string>()
    for (let k = 0; k < kacKural && kurallar.length < kacKural; k++) {
      let secilen: { metin: string; tanik: string[] } | null = null
      for (let deneme = 0; deneme < 120 && !secilen; deneme++) {
        const bas = rng.pick(BAS_HARFLER)
        if (gorulenBas.has(bas)) continue
        const uzunluk = p['uzunlukKisiti'] ? rng.int(4, 6) : 0
        const yasak = p['yasakHarf'] ? rng.pick(YASAKLAR) : ''
        if (yasak && kucuk(yasak) === kucuk(bas)) continue

        const tanik = NESNELER.filter((w) => {
          const f = kucuk(w)
          if (f[0] !== kucuk(bas)) return false
          if (uzunluk && w.length !== uzunluk) return false
          if (yasak && f.includes(kucuk(yasak))) return false
          return true
        })
        // Üç tanık: kuralın cevabı olduğunun kanıtı. Tanıklar
        // puanlamaya girmiyor, yalnızca kuralı ayakta tutuyor.
        if (tanik.length < 3) continue

        const parcalar = [`${bas} harfiyle başlayan`]
        if (uzunluk) parcalar.push(`${uzunluk} harfli`)
        if (yasak) parcalar.push(`içinde ${yasak} harfi geçmeyen`)
        gorulenBas.add(bas)
        secilen = { metin: `${parcalar.join(', ')} nesneler:`, tanik: [...tanik] }
      }
      if (secilen) kurallar.push(secilen)
    }

    const render: RenderNode[] = []
    const cevaplar: { id: string; kural: string }[] = []
    kurallar.forEach((kr, ki) => {
      render.push({ t: 'text', value: kr.metin, size: 'md', bold: true })
      render.push({
        t: 'rows',
        items: Array.from({ length: satir }, (_, i) => ({
          label: `${i + 1}.`,
          blank: true,
          id: `f${ki}_${i}`,
        })),
        columns: 2,
      })
      for (let i = 0; i < satir; i++) cevaplar.push({ id: `f${ki}_${i}`, kural: kr.metin })
    })

    return {
      code: 'word_fluency',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Her kural için aklına gelen nesneleri yaz. Tamamını doldurmak zorunda değilsin.',
      render,
      /**
       * Cevap anahtarı kapalı bir liste değil, ÖRNEK.
       *
       * Uygulayıcı sayarken "listede yok" diye eleme yapmasın diye
       * anahtarda bu açıkça yazıyor.
       */
      solution: {
        kurallar: kurallar.map((k) => ({ kural: k.metin, ornekler: k.tanik.slice(0, 6) })),
        slotlar: cevaplar.map((c) => c.id),
      },
      grading: 'manual',
      estimatedSeconds: kurallar.length * 90,
      itemCount: kurallar.length * satir,
    }
  },
  /**
   * Elle puanlanıyor; burada yapılan şey yalnızca BİÇİM denetimi.
   *
   * "Gerçek bir kelime mi, nesne mi" sorusunu 70 kelimelik bir tanık
   * listesiyle cevaplamak yanlış olur. Bu fonksiyon yine de var, çünkü
   * şablon sözleşmesi istiyor ve bir gün ekran kanalı açılırsa
   * uygulayıcıya ön sayım verecek.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { slotlar: string[] }
    const got = Array.isArray(answer) ? answer : []
    const items: ItemResult[] = sol.slotlar.map((id, i) => {
      const ham = got[i]
      const verilen = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      return {
        id,
        beklenen: 'kurala uyan bir nesne',
        verilen,
        // Biçim denetimi: boş değil ve en az üç harf. Anlamı uygulayıcı karara bağlıyor.
        dogru: verilen !== null && verilen.trim().length >= 3,
      }
    })
    const yazilan = items.filter((x) => x.dogru).length
    return {
      correct: yazilan,
      total: items.length,
      accuracy: items.length ? yazilan / items.length : 0,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  /**
   * Anahtar bir LİSTE değil, örnek.
   *
   * Uygulayıcı "listede yok" diye eleme yapmasın diye bu cümle
   * anahtarın içinde yazıyor: kuralı sağlayan her nesne doğru.
   */
  anahtar(instance) {
    const sol = instance.solution as { kurallar: { kural: string; ornekler: string[] }[] }
    const satirlar = sol.kurallar.map((k) => `${k.kural} ${k.ornekler.join(', ')}`)
    return `${satirlar.join('   |   ')}  (örnektir; kurala uyan her nesne doğru sayılır)`
  },
}
